/**
 * BodyMap — interactive SVG body for incident reports.
 *
 * 16 selectable regions tagged with semantic ids that match the backend
 * BodyMapLocation literal. Calm forest-green selection state matching
 * the existing palette — no new colors.
 */
import { useI18n } from "../lib/i18n";

const REGIONS = [
  { id: "head_front", d: "M 110 30 m -22 0 a 22 22 0 1 0 44 0 a 22 22 0 1 0 -44 0", labelXY: [140, 35] },
  { id: "head_back", d: "M 110 30 m -16 0 a 16 16 0 1 0 32 0 a 16 16 0 1 0 -32 0", labelXY: [80, 32], hiddenInSvg: true },
  { id: "face", d: "M 110 50 m -10 0 a 10 8 0 1 0 20 0 a 10 8 0 1 0 -20 0", labelXY: [80, 50] },
  { id: "neck", d: "M 100 60 L 120 60 L 120 75 L 100 75 Z", labelXY: [140, 70] },
  { id: "chest", d: "M 80 78 L 140 78 L 140 120 L 80 120 Z", labelXY: [165, 100] },
  { id: "abdomen", d: "M 85 122 L 135 122 L 135 158 L 85 158 Z", labelXY: [165, 140] },
  { id: "back", d: "M 85 78 L 135 78 L 135 158 L 85 158 Z", labelXY: [165, 100], hiddenInSvg: true },
  { id: "left_arm", d: "M 60 85 L 78 85 L 78 160 L 60 160 Z", labelXY: [30, 120] },
  { id: "right_arm", d: "M 142 85 L 160 85 L 160 160 L 142 160 Z", labelXY: [180, 120] },
  { id: "left_hand", d: "M 56 162 L 80 162 L 80 185 L 56 185 Z", labelXY: [30, 175] },
  { id: "right_hand", d: "M 140 162 L 164 162 L 164 185 L 140 185 Z", labelXY: [180, 175] },
  { id: "left_leg", d: "M 86 160 L 108 160 L 108 250 L 86 250 Z", labelXY: [55, 205] },
  { id: "right_leg", d: "M 112 160 L 134 160 L 134 250 L 112 250 Z", labelXY: [165, 205] },
  { id: "left_foot", d: "M 82 252 L 110 252 L 110 270 L 82 270 Z", labelXY: [50, 265] },
  { id: "right_foot", d: "M 110 252 L 138 252 L 138 270 L 110 270 Z", labelXY: [170, 265] },
  { id: "other", d: "", hiddenInSvg: true },
];

export default function BodyMap({ selected = [], onChange, readOnly = false }) {
  const { t } = useI18n();
  const toggle = (id) => {
    if (readOnly) return;
    const next = selected.includes(id)
      ? selected.filter((s) => s !== id)
      : [...selected, id];
    onChange?.(next);
  };
  return (
    <div className="flex flex-wrap items-start gap-6" data-testid="ops-body-map">
      <svg viewBox="0 0 220 280" className="w-48 sm:w-56 h-auto shrink-0">
        {REGIONS.filter((r) => !r.hiddenInSvg).map((r) => {
          const active = selected.includes(r.id);
          return (
            <g key={r.id} className="cursor-pointer" onClick={() => toggle(r.id)} data-testid={`body-map-region-${r.id}`}>
              <path
                d={r.d}
                fill={active ? "#1F3A2A" : "#FAF9F1"}
                stroke="#1F3A2A"
                strokeWidth="1.4"
                opacity={active ? 0.9 : 0.55}
              />
            </g>
          );
        })}
      </svg>
      <ul className="grid grid-cols-2 gap-1.5 text-xs">
        {REGIONS.map((r) => (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => toggle(r.id)}
              disabled={readOnly}
              data-testid={`body-map-chip-${r.id}`}
              className={`text-left px-2.5 py-1.5 rounded-full border transition-colors ${
                selected.includes(r.id)
                  ? "bg-[#1F3A2A] text-white border-[#1F3A2A]"
                  : "bg-white text-[#6B7280] border-[#EEF0F4] hover:border-[#1F3A2A]/40"
              }`}
            >
              {t(`ops.body_map.${r.id}`)}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
